import { useState } from "react";

import {
  almanacExtract,
  applyDecIncrement,
  applyGhaIncrement,
  dCorrectionMin,
  formatDeclination,
  formatDegMin,
  ghaIncrementMin,
  NOMINAL_HOURLY_RATE_DEG,
  vCorrectionMin,
  type AlmanacBody,
} from "@nav-games/astro";

/// Almanac "Increments and Corrections" calculator — the workflow the
/// Nautical Almanac's yellow pages teach.
///
/// Pick the body and a date/hour; the simulated daily-page extract
/// shows the hourly GHA, hourly Dec, plus the v (GHA deviation from
/// the nominal rate) and d (Dec rate) values. Set the minutes and
/// seconds past the hour, and the calculator walks the arithmetic
/// line-by-line — increment, v-correction, d-correction — exactly as
/// the student would on the pro forma worksheet.

const BODY_ORDER: readonly AlmanacBody[] = [
  "sun",
  "aries",
  "venus",
  "mars",
  "jupiter",
  "saturn",
  "moon",
];

const BODY_LABEL: Record<AlmanacBody, string> = {
  sun: "Sun",
  aries: "Aries (stars)",
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
  moon: "Moon",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function todayIso(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-${String(now.getUTCDate()).padStart(2, "0")}`;
}

function NumField({
  label,
  value,
  onChange,
  step = 1,
  min,
  max,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: number;
  min?: number;
  max?: number;
  hint?: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        flex: 1,
        minWidth: 110,
      }}
    >
      <span className="small muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        {...(min !== undefined ? { min } : {})}
        {...(max !== undefined ? { max } : {})}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function AlmanacIncrementCalculator() {
  const [body, setBody] = useState<AlmanacBody>("sun");
  const [dateStr, setDateStr] = useState(todayIso());
  const [hour, setHour] = useState("14");
  const [minute, setMinute] = useState("23");
  const [second, setSecond] = useState("17");

  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const h = Number(hour);
  const m = Number(minute);
  const s = Number(second);
  const valid =
    validDate &&
    Number.isFinite(h) &&
    h >= 0 &&
    h < 24 &&
    Number.isFinite(m) &&
    m >= 0 &&
    m < 60 &&
    Number.isFinite(s) &&
    s >= 0 &&
    s < 60;

  // Build the hour-start UT for the extract, and the actual sight
  // time for the optional reference value.
  const hourStart = valid
    ? new Date(`${dateStr}T${String(h).padStart(2, "0")}:00:00Z`)
    : new Date();

  const extract = almanacExtract(body, hourStart);
  const incMin = ghaIncrementMin(body, m, s);
  const vMin = vCorrectionMin(extract.vPerHourMin, m, s);
  const dMin = dCorrectionMin(extract.dPerHourMin, m, s);
  const ghaFinal = applyGhaIncrement({
    body,
    hourlyGhaDeg: extract.ghaDeg,
    m,
    s,
    vPerHourMin: extract.vPerHourMin,
  });
  const decFinal = applyDecIncrement({
    hourlyDecDeg: extract.decDeg,
    m,
    s,
    dPerHourMin: extract.dPerHourMin,
  });

  const fracHour = m / 60 + s / 3600;
  const rate = NOMINAL_HOURLY_RATE_DEG[body];

  // Format the increment in the canonical "deg min.tenths" form: e.g.
  // 5° 45.8' rather than the decimal-degree form.
  const incDeg = Math.floor(incMin / 60);
  const incMinRem = incMin - incDeg * 60;
  const incStr = `${incDeg}° ${incMinRem.toFixed(1)}′`;

  const hasV = extract.vPerHourMin !== 0;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Almanac increments & corrections</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        The "yellow pages" workflow — what the Nautical Almanac teaches.
        Pick the body and the moment of the sight; the simulated daily-page
        extract gives the hourly GHA, hourly Dec, plus v (GHA deviation
        from the nominal rate) and d (Dec rate). The arithmetic below
        walks the increment, v-correction and d-correction the way a
        student fills in the pro forma.
      </p>

      {/* Body */}
      <div className="small muted" style={{ marginBottom: 4 }}>
        Body
      </div>
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        {BODY_ORDER.map((b) => (
          <button
            key={b}
            type="button"
            className={body === b ? "" : "secondary"}
            onClick={() => setBody(b)}
            style={{ flex: "1 1 90px", fontSize: 14, padding: "8px 6px" }}
            aria-pressed={body === b}
          >
            {BODY_LABEL[b]}
          </button>
        ))}
      </div>

      {/* Date + time */}
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          marginBottom: 8,
        }}
      >
        <span className="small muted">Date (UTC)</span>
        <input
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          style={inputStyle}
          aria-label="Date"
        />
      </label>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <NumField
          label="Hour (UT)"
          value={hour}
          onChange={setHour}
          step={1}
          min={0}
          max={23}
        />
        <NumField
          label="Minute"
          value={minute}
          onChange={setMinute}
          step={1}
          min={0}
          max={59}
        />
        <NumField
          label="Second"
          value={second}
          onChange={setSecond}
          step={1}
          min={0}
          max={59}
        />
      </div>

      {/* Extract panel — what the daily page shows. */}
      <div
        style={{
          padding: 12,
          marginBottom: 12,
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--border)",
          background: "var(--bg-elev)",
        }}
      >
        <p
          className="small muted"
          style={{ margin: "0 0 8px", fontWeight: 600 }}
        >
          Daily-page extract — {BODY_LABEL[body]} at {String(h).padStart(2, "0")}h
          {valid && (
            <>
              {" "}on {dateStr} UT
            </>
          )}
        </p>
        <table
          className="small"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          <tbody>
            <tr>
              <td style={{ padding: 2 }}>GHA ({String(h).padStart(2, "0")}h)</td>
              <td style={{ padding: 2 }}>{formatDegMin(extract.ghaDeg, 1)}</td>
            </tr>
            <tr>
              <td style={{ padding: 2 }}>
                Dec ({String(h).padStart(2, "0")}h)
              </td>
              <td style={{ padding: 2 }}>
                {body === "aries"
                  ? "(stars use SHA + GHA Aries — no Dec on this column)"
                  : formatDeclination(extract.decDeg, 1)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: 2 }}>v</td>
              <td style={{ padding: 2 }}>
                {hasV ? `${extract.vPerHourMin.toFixed(1)}′/hr` : "—"}
              </td>
            </tr>
            <tr>
              <td style={{ padding: 2 }}>d</td>
              <td style={{ padding: 2 }}>
                {body === "aries"
                  ? "—"
                  : `${extract.dPerHourMin >= 0 ? "+" : ""}${extract.dPerHourMin.toFixed(1)}′/hr`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Working — the student's arithmetic, line by line. */}
      <div
        className="small"
        style={{
          padding: 12,
          marginBottom: 12,
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--border)",
          background: "var(--bg-elev)",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          whiteSpace: "pre-wrap",
          lineHeight: 1.5,
        }}
      >
{`Working — corrected for ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s
─────────────────────────────────────────────
GHA increment at nominal rate (${rate}°/hr):
  Increment = ${rate}°/hr · (${m}m ${s}s / 60)
            = ${rate}° · ${fracHour.toFixed(5)}
            = ${incStr}    (${incMin.toFixed(1)}′)
${
  hasV
    ? `
v-correction (deviation from nominal):
  v-corr   = ${extract.vPerHourMin.toFixed(1)}′/hr · ${fracHour.toFixed(5)}
            = ${vMin.toFixed(1)}′`
    : "\nv-correction:  none (Sun and Aries are pre-adjusted, v = 0)"
}

GHA at sight time:
  GHA(h:m:s) = GHA(h:00) + increment${hasV ? " + v-corr" : ""}
             = ${formatDegMin(extract.ghaDeg, 1)} + ${incMin.toFixed(1)}′${
               hasV ? ` + ${vMin.toFixed(1)}′` : ""
             }
             = ${formatDegMin(ghaFinal, 1)}
${
  body === "aries"
    ? ""
    : `
d-correction:
  d-corr   = ${extract.dPerHourMin >= 0 ? "+" : ""}${extract.dPerHourMin.toFixed(1)}′/hr · ${fracHour.toFixed(5)}
            = ${dMin >= 0 ? "+" : ""}${dMin.toFixed(1)}′

Dec at sight time:
  Dec(h:m:s) = Dec(h:00) + d-corr
             = ${formatDeclination(extract.decDeg, 1)} + (${dMin >= 0 ? "+" : ""}${dMin.toFixed(1)}′)
             = ${formatDeclination(decFinal, 1)}`
}`}
      </div>

      {!valid && (
        <p className="small muted" style={{ margin: 0 }}>
          Enter a valid date and time (hour 0–23, minute 0–59, second 0–59).
        </p>
      )}
    </div>
  );
}
